<template>
  <b-container fluid>
    <b-breadcrumb :items="breadcrumbs" />
    <b-row>
      <b-col md="4">
        <b-card header="Build a Report">
          <p>Start building your report by selecting the workflow, and report type.</p>
        </b-card>
      </b-col>
      <b-col>
        <b-card v-if="workflows" title="Build a Report" class="mb-3">
          <b-form v-on:submit.prevent.stop="submit">
            <b-form-group label="Report Name">
              <b-form-input v-model="reportName" required></b-form-input>
            </b-form-group>
            <h4>Choose Workflow</h4>
            <b-form-group v-if="workflows" id="workflow_label" label="Workflow" label-for="workflow">
              <b-form-select id="workflow" :options="workflowOptions" v-model="workflow">
                <template slot="first">
                  <option :value="null">All Workflows</option>
                </template>
              </b-form-select>
              <span slot="description">
                More column and filter options are available if a specific workflow is selected.
              </span>
            </b-form-group>
            <h4>Choose Report Type</h4>
            <b-form-group>
              <b-form-radio v-model="reportType" value="table">Table</b-form-radio>
              <span slot="description">
                Create a report with tabular results, that can be viewed as a table in a web browser, or downloaded as a spreadsheet.
              </span>
            </b-form-group>
            <b-form-group>
              <b-form-radio v-model="reportType" value="donut">Donut Chart</b-form-radio>
              <span slot="description">
                Similar to a pie chart but more legible, a donut chart shows the breakdown of a single field into its constituent values.
              </span>
            </b-form-group>
            <b-form-group>
              <b-form-radio v-model="reportType" value="line">Line Chart</b-form-radio>
              <span slot="description">
                A line chart shows the progression of a single value over time.
              </span>
            </b-form-group>
            <b-form-group>
              <b-form-radio v-model="reportType" value="bar">Bar Chart</b-form-radio>
              <span slot="description">
                A bar chart shows data values as vertical bars.
              </span>
            </b-form-group>
            <b-form-group>
              <b-form-radio v-model="reportType" value="stacked">Stacked Bar Chart</b-form-radio>
              <span slot="description">
                A bar chart shows data values as vertical bars, each split into smaller pieces.
              </span>
            </b-form-group>
            <div v-if="showGrouping">
              <hr>
              <h4>{{groupingHeading}}</h4>
              <column-editor :column="grouping" :columnSpecCategories="reportOptions.column_categories" :reportType="reportType" :isGrouping="true" :isAggregate="false" :isRequired="reportType !== 'table'" :hasLabel="true" :prepend="groupingPrepend" :defaultOptionText="groupingDefaultOptionText">
              </column-editor>
              <small class="form-text">{{groupingDescription}}</small>
            </div>
            <div v-if="showSubGrouping">
              <hr>
              <h4>{{subGroupingHeading}}</h4>
              <column-editor :column="subGrouping" :columnSpecCategories="reportOptions.column_categories" :reportType="reportType" :isRequired="isSubGroupingRequired" :isGrouping="true" :isSubGrouping="true" :isAggregate="false" :hasLabel="true" :prepend="subGroupingPrepend" :defaultOptionText="subGroupingDefaultOptionText">
              </column-editor>
              <small class="form-text">{{subGroupingDescription}}</small>
            </div>
            <div v-if="showChartValue">
              <hr>
              <h4>{{chartValueHeading}}</h4>
              <column-editor :column="chartValue" :columnSpecCategories="reportOptions.column_categories" :reportType="reportType" :isRequired="true" :isGrouping="false" :isAggregate="true" :hasLabel="true" :prepend="chartValuePrepend" :defaultOptionText="chartValueDefaultOptionText" :allowedDataTypes="['numeric']">
              </column-editor>
              <small class="form-text">{{chartValueDescription}}</small>
            </div>
            <div class="text-right">
              <b-button type="submit" variant="primary">Next</b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from 'lodash'
import API from '@/lib/api'
import ColumnEditor from '@/components/reports/ColumnEditor'

export default {
  name: 'CreateReport',
  components: { ColumnEditor },
  data () {
    return {
      admin: this.$route.query.admin,
      reportName: 'My Report',
      reportOptions: null,
      workflows: null,
      workflow: null,
      reportType: null,
      grouping: { column_id: null, sub_columns: [], label: null, options: {} },
      subGrouping: { column_id: null, sub_columns: [], label: null, options: {} },
      chartValue: { column_id: null, sub_columns: [], label: null, options: {} }
    }
  },
  mounted () {
    this.fetchWorkflows()
    this.fetchOptions()
  },
  watch: {
    workflow () {
      this.fetchOptions()
    },
    reportType () {
      this.grouping = { column_id: null, sub_columns: [], label: null, options: {} }
      this.subGrouping = { column_id: null, sub_columns: [], label: null, options: {} }
      this.chartValue = { column_id: null, sub_columns: [], label: null, options: {} }
    }
  },
  computed: {
    breadcrumbs () {
      if (this.admin) {
        return [
          {text: 'My Vos Voco', to: '/'},
          {text: 'Admin', to: '/admin'},
          {text: 'Reports', to: '/admin/reports'},
          {text: 'Build a Report', to: '/reports/new?admin=true'}
        ]
      } else {
        return [
          {text: 'My Vos Voco', to: '/'},
          {text: 'Reports', to: '/reports'},
          {text: 'Build a Report', to: '/reports/new'}
        ]
      }
    },
    workflowOptions () {
      return this.workflows.map((workflow) => { return { text: workflow.name, value: workflow } })
    },
    showGrouping () {
      return this.reportType != null && this.reportOptions != null
    },
    groupingHeading () {
      return {
        table: 'Table Row Grouping',
        donut: 'Donut Pieces',
        line: 'X Axis',
        bar: 'X Axis: Bars',
        stacked: 'X Axis: Bars'
      }[this.reportType]
    },
    groupingDefaultOptionText () {
      if (this.reportType === 'table') {
        return 'Vos Voco event'
      } else {
        return 'Please select an option'
      }
    },
    groupingDescription () {
      return {
        table: 'Events will be grouped into one line per each of the above.',
        donut: 'Each piece of the donut will represent one of the above.',
        line: 'Each tick of the X axis will represent one of the above.',
        bar: 'Each bar will represent one of the above.',
        stacked: 'Each bar will represent one of the above.'
      }[this.reportType]
    },
    groupingPrepend () {
      return {
        table: 'One row per:',
        donut: 'One piece per:',
        line: 'One tick per:',
        bar: 'One bar per:',
        stacked: 'One bar per:'
      }[this.reportType]
    },
    showSubGrouping () {
      return this.reportOptions != null && (this.reportType === 'stacked')
    },
    isSubGroupingRequired () {
      return this.reportType !== 'line'
    },
    subGroupingDefaultOptionText () {
      if (this.reportType === 'line') {
        return 'Single line only'
      } else {
        return 'Please select an option'
      }
    },
    subGroupingHeading () {
      return {
        line: 'Lines',
        stacked: 'Bar Segments'
      }[this.reportType]
    },
    subGroupingDescription () {
      return {
        line: 'Each coloured line will represent one of the above.',
        stacked: 'Each segment of a bar will represent one of the above.'
      }[this.reportType]
    },
    subGroupingPrepend () {
      return {
        line: 'One line per:',
        stacked: 'One segment per:'
      }[this.reportType]
    },
    showChartValue () {
      return (this.reportOptions != null && this.reportType != null && this.reportType !== 'table')
    },
    chartValueDefaultOptionText () {
      return 'Please select an option'
    },
    chartValueHeading () {
      return {
        donut: 'Piece size',
        line: 'Y Axis',
        bar: 'Y Axis: Bar Size',
        stacked: 'Y Axis: Segment Size'
      }[this.reportType]
    },
    chartValueDescription () {
      return {
        donut: 'The size of each piece of the donut will be determined by the above.',
        line: 'The height of each point in the line will be determined by the above.',
        bar: 'The height of each bar will be determined by the above.',
        stacked: 'The height of each segment will be determined by the above.'
      }[this.reportType]
    },
    chartValuePrepend () {
      return {
        donut: 'Size:',
        line: 'Height:',
        bar: 'Size:',
        stacked: 'Size:'
      }[this.reportType]
    }
  },
  methods: {
    fetchWorkflows () {
      var context = this.admin ? 'admin' : 'company'
      API.get('workflows', {context: context}, this.$store.getters.authHeaders)
        .then((response) => {
          this.workflows = response.data.workflows
          if (!this.admin) {
            this.workflow = this.workflows[0]
          }
        })
    },
    fetchOptions () {
      this.reportOptions = null
      var workflowId = this.workflow != null ? this.workflow.id : null
      API.get('reports/options', {workflow_id: workflowId, admin: this.admin}, this.$store.getters.authHeaders)
        .then((response) => {
          this.reportOptions = response.data
        })
    },
    columnSettingsToParams (settings, options) {
      if (options == null) {
        options = {}
      }
      return _.merge(options, settings)
    },
    defaultColumnParams (groupParams) {
      if (this.grouping.column_id) {
        return groupParams.concat(this.reportOptions.default_grouped_columns)
      } else {
        return this.reportOptions.default_ungrouped_columns
      }
    },
    toParams () {
      var params = {
        name: this.reportName,
        type: this.reportType
      }
      if (this.workflow) {
        params['workflow_id'] = this.workflow.id
      }
      params['group'] = []
      if (this.grouping.column_id) {
        params['group'].push(this.columnSettingsToParams(this.grouping))
        if (this.subGrouping.column_id) {
          params['group'].push(this.columnSettingsToParams(this.subGrouping))
        }
      }
      if (this.chartValue.column_id) {
        params['chart_select'] = this.columnSettingsToParams(this.chartValue)
      }
      params['filters'] = [{
        type: 'logged_at',
        range_type: 'recent',
        period_type: 'weeks',
        count: 2,
        status: 'open'
      }]
      params['settings'] = this.defaultReportSettings()
      if (this.reportType === 'table') {
        params['columns'] = this.defaultColumnParams(params['group'])
      }
      return params
    },
    defaultSortOrder () {
      if (this.grouping.column_id == null) {
        return [{ column_id: 'logged_at', direction: 'descending' }]
      } else if (this.reportType === 'donut') {
        return [this.columnSettingsToParams(this.chartValue, { direction: 'descending' })]
      } else {
        if (this.subGrouping.column != null) {
          return [
            this.columnSettingsToParams(this.grouping, { direction: 'ascending' }),
            this.columnSettingsToParams(this.subGrouping, { direction: 'ascending' })
          ]
        } else {
          return [this.columnSettingsToParams(this.grouping, { direction: 'ascending' })]
        }
      }
    },
    defaultReportSettings () {
      var settings = { order: this.defaultSortOrder() }
      switch (this.reportType) {
        case 'donut':
          settings.max = 10
          break
        case 'line':
          settings.show_trend = false
          break
        case 'table':
          settings.page_size = 25
          break
      }
      return settings
    },
    submit () {
      const reportParams = this.toParams()
      this.$router.push({path: '/reports/run', query: {admin: this.admin, report: reportParams}})
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/reports/CreateReport.vue